/* eslint-disable jsx-a11y/anchor-is-valid */
import React, {useEffect, useRef} from 'react'
import ApexCharts from 'apexcharts'

import {getCSS, getCSSVariableValue} from '../../../../_metronic/assets/ts/_utils'
import {useThemeMode} from '../../../../_metronic/partials'

const ParameterChartWidget = ({className, chartData, chartStep, setChartStep, chartTotal}) => {
  const chartRef = useRef(null)
  const {mode} = useThemeMode()

  useEffect(() => {
    if (chartRef.current && chartData.length > 0) {
      const chart = new ApexCharts(chartRef.current, getChartOptions(chartData))
      chart.render()
      return () => chart.destroy()
    }
  }, [chartData])

  return (
    <div className={`card ${className} shadow`}>
      <div className='card-header border-0 pt-5'>
        <h3 className='card-title align-items-start flex-column'>
          <span className='card-label fw-bold fs-3 mb-1'>Recent Transactions</span>
          <span className='text-muted fw-semibold fs-7'>More than 1000 new records</span>
        </h3>

        <div className='card-toolbar' data-kt-buttons='true'>
          <div
            className={`btn btn-sm btn-color-muted btn-active btn-active-primary px-4 me-1 ${
              chartStep == '6hr' ? 'active' : ''
            }`}
            onClick={() => setChartStep('6hr')}
          >
            6 Hours
          </div>
          <a
            className={`btn btn-sm btn-color-muted btn-active btn-active-primary px-4 me-1 ${
              chartStep == '1dayAvg' ? 'active' : ''
            }`}
            onClick={() => setChartStep('1dayAvg')}
          >
            1 Day / Hour
          </a>
          <a
            className={`btn btn-sm btn-color-muted btn-active btn-active-primary px-4 me-1 ${
              chartStep == '7dayAvg' ? 'active' : ''
            }`}
            onClick={() => setChartStep('7dayAvg')}
          >
            7 Days / Hour
          </a>
        </div>
      </div>

      <div className='card-body'>
        <div ref={chartRef} id='kt_charts_widget_3_chart'></div>

        {chartTotal && (
          <div
            className='d-flex justify-content-between align-items-center'
            style={{marginTop: '-20px'}}
          >
            <div className='d-flex align-items-center'>
              <div className=''>
                <span className='text-muted fs-7 fw-semibold'>Count : </span>
                <span className='text-dark fs-7 fw-bold ms-1'>{chartTotal?.count}</span>
              </div>
              <div className='ms-5'>
                <span className='text-muted fs-7 fw-semibold'>Average : </span>
                <span className='text-dark fs-7 fw-bold ms-1'>{chartTotal?.avg}</span>
              </div>

              <div className='ms-5'>
                <span className='text-muted fs-7 fw-semibold'>Min : </span>
                <span className='text-dark fs-7 fw-bold ms-1'>{chartTotal?.min}</span>
              </div>

              <div className='ms-5'>
                <span className='text-muted fs-7 fw-semibold'>Max : </span>
                <span className='text-dark fs-7 fw-bold ms-1'>{chartTotal?.max}</span>
              </div>

              <div className='ms-5'>
                <span className='text-muted fs-7 fw-semibold'>Sum : </span>
                <span className='text-dark fs-7 fw-bold ms-1'>{chartTotal?.sum}</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export {ParameterChartWidget}

function getChartOptions(chartData) {
  const labelColor = getCSSVariableValue('--bs-gray-500')
  const borderColor = getCSSVariableValue('--bs-gray-200')
  const baseColor = getCSSVariableValue('--bs-info')
  const lightColor = getCSSVariableValue('--bs-info-light')

  const sortedData = [...chartData].sort((a, b) => a.t - b.t)

  return {
    series: [
      {
        name: 'Value',
        data: sortedData.map((item) => parseFloat(item.v).toFixed(2)), // แปลง v เป็นทศนิยม 2 ตำแหน่ง
      },
    ],
    chart: {
      fontFamily: 'inherit',
      type: 'area',
      height: 350,
      toolbar: {
        show: false,
      },
    },
    plotOptions: {},
    legend: {
      show: false,
    },
    dataLabels: {
      enabled: false,
    },
    fill: {
      type: 'solid',
      opacity: 1,
    },
    stroke: {
      curve: 'smooth',
      show: true,
      width: 3,
      colors: [baseColor],
    },
    xaxis: {
      categories: sortedData.map(
        (item) => new Date(item.t * 1000).toLocaleTimeString() // แสดงเวลาเป็นแกน X
      ),
      tickAmount: Math.min(10, sortedData.length),
      axisBorder: {
        show: false,
      },
      axisTicks: {
        show: false,
      },
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
      },
      crosshairs: {
        position: 'front',
        stroke: {
          color: baseColor,
          width: 1,
          dashArray: 3,
        },
      },
      tooltip: {
        enabled: true,
        formatter: undefined,
        offsetY: 0,
        style: {
          fontSize: '12px',
        },
      },
    },
    yaxis: {
      labels: {
        style: {
          colors: labelColor,
          fontSize: '12px',
        },
        formatter: function (value) {
          return parseFloat(value).toFixed(2) // แสดงค่าบนแกน Y เป็นทศนิยม 2 ตำแหน่ง
        },
      },
    },
    states: {
      normal: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      hover: {
        filter: {
          type: 'none',
          value: 0,
        },
      },
      active: {
        allowMultipleDataPointsSelection: false,
        filter: {
          type: 'none',
          value: 0,
        },
      },
    },
    tooltip: {
      style: {
        fontSize: '12px',
      },
      y: {
        formatter: function (value) {
          return `${parseFloat(value).toFixed(2)}` // แสดง tooltip เป็นทศนิยม 2 ตำแหน่ง
        },
      },
    },
    colors: [lightColor],
    grid: {
      borderColor: borderColor,
      strokeDashArray: 4,
      yaxis: {
        lines: {
          show: true,
        },
      },
    },
    markers: {
      strokeColors: baseColor,
      strokeWidth: 3,
    },
  }
}
